import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Users, Shield, Activity, Database, Server, Settings, AlertTriangle, CheckCircle, Rocket, Plus, Trash2, GripVertical, Save, Loader2, LayoutDashboard, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { OnboardingWidget } from '../components/onboarding';
import { CompetitionZoneWidget } from '../components/widgets/CompetitionZoneWidget';
import { FivePillarsWidget } from '../components/widgets/FivePillarsWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Section display names for the editor
const SECTION_NAMES = {
  welcome_banner: 'Welcome Banner',
  key_metrics: 'Key Metrics',
  upcoming_events: 'Upcoming Events',
  recent_activity: 'Recent Activity',
  whats_coming_online: "What's Coming Online"
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // System Stats State (live data for dashboard cards)
  const [systemStats, setSystemStats] = useState({
    total_users: 0,
    new_users_this_week: 0,
    active_sessions: 0,
    system_health: { status: 'loading', uptime_percent: 0, message: 'Loading...' },
    database_status: { status: 'loading', message: 'Checking connection...' },
    last_updated: null
  });
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Default Dashboard Editor state
  const [dashboardEditorOpen, setDashboardEditorOpen] = useState(false);
  const [systemDefault, setSystemDefault] = useState(null);
  const [editingLayout, setEditingLayout] = useState([]);
  const [welcomeConfig, setWelcomeConfig] = useState({
    title: 'Welcome to RhinoShield Platform',
    description: 'Your mission control center for managing operations, tracking performance, and driving results.'
  });
  const [dashboardSaving, setDashboardSaving] = useState(false);
  
  // Pre-Release Dashboard Editor state
  const [preReleaseConfig, setPreReleaseConfig] = useState({
    enabled: true,
    title: "What's Coming Online",
    items: []
  });
  const [preReleaseLoading, setPreReleaseLoading] = useState(true);
  const [preReleaseSaving, setPreReleaseSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, statsRes] = await Promise.all([
          axios.get(`${API}/users`),
          axios.get(`${API}/dashboard/stats`)
        ]);
        setUsers(usersRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
        toast.error('Failed to load admin dashboard');
      } finally {
        setLoading(false);
      }
    };

    // Fetch live system stats for dashboard cards
    const fetchSystemStats = async () => {
      try {
        const res = await axios.get(`${API}/admin-dashboard/system-stats`);
        setSystemStats(res.data);
      } catch (error) {
        console.error('Failed to fetch system stats:', error);
        // Set error state for cards
        setSystemStats(prev => ({
          ...prev,
          system_health: { status: 'error', uptime_percent: 0, message: 'Failed to load' },
          database_status: { status: 'error', message: 'Failed to check' }
        }));
      } finally {
        setStatsLoading(false);
      }
    };

    const fetchPreReleaseConfig = async () => {
      try {
        const res = await axios.get(`${API}/platform/dashboard-status`);
        setPreReleaseConfig(res.data);
      } catch (error) {
        console.error('Failed to fetch pre-release config:', error);
      } finally {
        setPreReleaseLoading(false);
      }
    };

    const fetchWelcomeConfig = async () => {
      try {
        const res = await axios.get(`${API}/platform/welcome-config`);
        if (res.data) {
          setWelcomeConfig(res.data);
        }
      } catch (error) {
        console.error('Failed to fetch welcome config:', error);
      }
    };

    fetchData();
    fetchSystemStats();
    fetchPreReleaseConfig();
    fetchWelcomeConfig();
    
    // Refresh system stats every 30 seconds for live updates
    const statsInterval = setInterval(fetchSystemStats, 30000);
    return () => clearInterval(statsInterval);
  }, []);

  // Default Dashboard Editor handlers
  const openDashboardEditor = async () => {
    try {
      const res = await axios.get(`${API}/dashboards/admin/system-default`);
      setSystemDefault(res.data);
      setEditingLayout(res.data.layout || []);
      setDashboardEditorOpen(true);
    } catch (error) {
      toast.error('Failed to load system default dashboard');
    }
  };

  const handleDashboardLayoutDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(editingLayout);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Recalculate y positions based on order
    let currentY = 0;
    const updatedItems = items.map((item, index) => {
      const newItem = { ...item, y: currentY };
      // Full width items take 2 rows, others share rows
      if (item.w === 12) {
        currentY += item.h;
      } else if (index % 3 === 2 || index === items.length - 1) {
        currentY += item.h;
      }
      return newItem;
    });
    
    setEditingLayout(updatedItems);
  };

  const updateSectionWidth = (sectionId, width) => {
    setEditingLayout(prev => prev.map(item => 
      item.section_id === sectionId ? { ...item, w: width } : item
    ));
  };

  const saveDashboardDefault = async () => {
    setDashboardSaving(true);
    try {
      // Save the layout
      await axios.put(`${API}/dashboards/admin/system-default`, {
        layout: editingLayout
      });
      
      // Save the welcome config
      await axios.put(`${API}/platform/welcome-config`, welcomeConfig);
      
      toast.success('Default dashboard saved globally!');
      setDashboardEditorOpen(false);
    } catch (error) {
      toast.error('Failed to save default dashboard');
    } finally {
      setDashboardSaving(false);
    }
  };

  // Pre-Release Dashboard handlers
  const handlePreReleaseSave = async () => {
    setPreReleaseSaving(true);
    try {
      await axios.put(`${API}/platform/dashboard-status`, preReleaseConfig);
      toast.success('Pre-release dashboard updated successfully');
    } catch (error) {
      toast.error('Failed to save pre-release dashboard settings');
    } finally {
      setPreReleaseSaving(false);
    }
  };

  const addPreReleaseItem = () => {
    setPreReleaseConfig({
      ...preReleaseConfig,
      items: [...preReleaseConfig.items, { label: '', status: 'Planned' }]
    });
  };

  const removePreReleaseItem = (index) => {
    setPreReleaseConfig({
      ...preReleaseConfig,
      items: preReleaseConfig.items.filter((_, i) => i !== index)
    });
  };

  const updatePreReleaseItem = (index, field, value) => {
    const newItems = [...preReleaseConfig.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setPreReleaseConfig({ ...preReleaseConfig, items: newItems });
  };

  const handlePreReleaseDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(preReleaseConfig.items);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setPreReleaseConfig({ ...preReleaseConfig, items });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground dark:text-slate-400">Loading admin dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Helper function to get health status icon and color
  const getHealthStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'connected':
        return <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />;
      case 'degraded':
        return <AlertTriangle className="h-5 w-5 text-amber-500 dark:text-amber-400" />;
      case 'unhealthy':
      case 'error':
      case 'disconnected':
        return <AlertTriangle className="h-5 w-5 text-red-500 dark:text-red-400" />;
      case 'loading':
        return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
      default:
        return <CheckCircle className="h-5 w-5 text-slate-400" />;
    }
  };

  return (
    <Layout>
      <div className="space-y-6 p-6" data-testid="admin-dashboard">
        {/* Admin Header */}
        <div className="flex items-center space-x-3">
          <Shield className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          <div>
            <h1 className="text-3xl font-bold dark:text-white">Super Admin Dashboard</h1>
            <p className="text-muted-foreground dark:text-slate-400">System overview and management</p>
          </div>
        </div>

        {/* System Health Cards - LIVE DATA */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Users Card */}
          <Card 
            data-testid="total-users-card" 
            className="relative overflow-hidden border-2 border-blue-500 dark:border-blue-400 dark:bg-slate-800/60 transition-all duration-300 hover:border-blue-400"
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium dark:text-slate-200">Total Users</CardTitle>
              <Users className="h-4 w-4 text-blue-500 dark:text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold dark:text-white">
                {statsLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : systemStats.total_users}
              </div>
              <p className="text-xs text-muted-foreground dark:text-slate-400">
                {systemStats.new_users_this_week > 0 
                  ? `+${systemStats.new_users_this_week} this week` 
                  : 'No new users this week'}
              </p>
            </CardContent>
          </Card>

          {/* Active Sessions Card */}
          <Card 
            data-testid="active-sessions-card"
            className="relative overflow-hidden border-2 border-blue-500 dark:border-blue-400 dark:bg-slate-800/60 transition-all duration-300 hover:border-blue-400"
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium dark:text-slate-200">Active Sessions</CardTitle>
              <Activity className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold dark:text-white">
                {statsLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : systemStats.active_sessions}
              </div>
              <p className="text-xs text-muted-foreground dark:text-slate-400">
                Users active in last 5 min
              </p>
            </CardContent>
          </Card>

          {/* System Health Card */}
          <Card 
            data-testid="system-health-card"
            className="relative overflow-hidden border-2 border-blue-500 dark:border-blue-400 dark:bg-slate-800/60 transition-all duration-300 hover:border-blue-400"
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium dark:text-slate-200">System Health</CardTitle>
              <Server className="h-4 w-4 text-purple-500 dark:text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                {getHealthStatusIcon(systemStats.system_health?.status)}
                <span className="text-2xl font-bold dark:text-white">
                  {statsLoading ? '...' : `${systemStats.system_health?.uptime_percent || 0}%`}
                </span>
              </div>
              <p className="text-xs text-muted-foreground dark:text-slate-400">
                {systemStats.system_health?.message || 'Checking status...'}
              </p>
            </CardContent>
          </Card>

          {/* Database Status Card */}
          <Card 
            data-testid="database-status-card"
            className="relative overflow-hidden border-2 border-blue-500 dark:border-blue-400 dark:bg-slate-800/60 transition-all duration-300 hover:border-blue-400"
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium dark:text-slate-200">Database Status</CardTitle>
              <Database className="h-4 w-4 text-orange-500 dark:text-orange-400" />
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                {getHealthStatusIcon(systemStats.database_status?.status)}
                <span className="text-2xl font-bold dark:text-white">
                  {statsLoading ? '...' : (systemStats.database_status?.status === 'connected' ? 'OK' : 'ERR')}
                </span>
              </div>
              <p className="text-xs text-muted-foreground dark:text-slate-400">
                {systemStats.database_status?.message || 'Checking connection...'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Five Pillars Governance Dashboard */}
        <FivePillarsWidget isAdmin={true} compact={false} />

        {/* Default Dashboard Editor Card */}
        <Card className="dark:bg-slate-800/60 dark:border-slate-700 border-2 border-blue-500/50" data-testid="default-dashboard-editor">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <LayoutDashboard className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <CardTitle className="dark:text-white">Default Dashboard Editor</CardTitle>
                  <CardDescription className="dark:text-slate-400">
                    Configure the default dashboard layout and welcome message for all users
                  </CardDescription>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/admin/dashboard-editor')}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="open-dashboard-editor-btn"
              >
                <Eye className="h-4 w-4 mr-2" />
                Open Full Editor
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
              <div className="flex-1">
                <p className="text-sm font-medium dark:text-white">System Default Dashboard</p>
                <p className="text-xs text-muted-foreground dark:text-slate-400">
                  This layout is shown to all new users. Changes here propagate globally.
                </p>
              </div>
              <Badge variant="outline" className="text-blue-600 border-blue-500">
                Global
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Pre-Release Dashboard Editor */}
        <Card className="dark:bg-slate-800/60 dark:border-slate-700 border-2 border-amber-500/50" data-testid="pre-release-editor">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Rocket className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <CardTitle className="dark:text-white">Pre-Release Dashboard Editor</CardTitle>
                  <CardDescription className="dark:text-slate-400">
                    Configure the &quot;What&apos;s Coming Online&quot; panel visible to all users
                  </CardDescription>
                </div>
              </div>
              <Button 
                onClick={handlePreReleaseSave} 
                disabled={preReleaseSaving}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="save-prerelease-btn"
              >
                {preReleaseSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {preReleaseLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
              </div>
            ) : (
              <>
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <div>
                    <Label className="text-base font-medium dark:text-white">Show Panel to Users</Label>
                    <p className="text-sm text-muted-foreground dark:text-slate-400">
                      When enabled, all users see this panel on their dashboard
                    </p>
                  </div>
                  <Switch
                    checked={preReleaseConfig.enabled}
                    onCheckedChange={(checked) => setPreReleaseConfig({ ...preReleaseConfig, enabled: checked })}
                    data-testid="prerelease-enabled-toggle"
                  />
                </div>

                {/* Panel Title */}
                <div>
                  <Label className="dark:text-slate-300">Panel Title</Label>
                  <Input
                    value={preReleaseConfig.title}
                    onChange={(e) => setPreReleaseConfig({ ...preReleaseConfig, title: e.target.value })}
                    placeholder="What's Coming Online"
                    className="mt-1 dark:bg-slate-900 dark:border-slate-700"
                    data-testid="prerelease-title-input"
                  />
                </div>

                {/* Status Items */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="dark:text-slate-300">Module Status Items</Label>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={addPreReleaseItem}
                      className="dark:border-slate-600"
                      data-testid="add-prerelease-item-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Item
                    </Button>
                  </div>
                  
                  <DragDropContext onDragEnd={handlePreReleaseDragEnd}>
                    <Droppable droppableId="prerelease-items">
                      {(provided, snapshot) => (
                        <div 
                          className={`space-y-2 p-3 rounded-lg border-2 border-dashed transition-colors min-h-[100px] ${
                            snapshot.isDraggingOver 
                              ? 'border-amber-500 bg-amber-500/5' 
                              : 'border-slate-300 dark:border-slate-600'
                          }`}
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {preReleaseConfig.items?.map((item, index) => (
                            <Draggable key={`item-${index}`} draggableId={`item-${index}`} index={index}>
                              {(provided, snapshot) => (
                                <div 
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border dark:border-slate-700 ${snapshot.isDragging ? 'shadow-lg ring-2 ring-amber-500/50' : ''}`}
                                >
                                  <div {...provided.dragHandleProps}>
                                    <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab active:cursor-grabbing" />
                                  </div>
                                  <Input
                                    value={item.label}
                                    onChange={(e) => updatePreReleaseItem(index, 'label', e.target.value)}
                                    placeholder="Module name (e.g., Sales Hub)"
                                    className="flex-1 dark:bg-slate-800 dark:border-slate-600"
                                  />
                                  <select
                                    value={item.status}
                                    onChange={(e) => updatePreReleaseItem(index, 'status', e.target.value)}
                                    className="h-9 px-3 rounded-md border border-input bg-background text-sm dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                                  >
                                    <option value="Planned">Planned</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="Internal Testing">Internal Testing</option>
                                    <option value="Beta">Beta</option>
                                    <option value="Live">Live</option>
                                  </select>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    onClick={() => removePreReleaseItem(index)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          
                          {preReleaseConfig.items?.length === 0 && (
                            <div className="text-center py-6 text-muted-foreground dark:text-slate-400">
                              <Rocket className="h-8 w-8 mx-auto mb-2 opacity-50" />
                              <p>No items yet. Click &quot;Add Item&quot; to start.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>

                {/* Preview */}
                <div className="border-t pt-4 dark:border-slate-700">
                  <Label className="dark:text-slate-300 text-sm">Preview (as users will see it)</Label>
                  <div className="mt-2 p-4 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-lg border dark:border-slate-700">
                    <div className="flex items-center gap-2 mb-3">
                      <Rocket className="h-4 w-4 text-primary" />
                      <span className="font-medium text-sm dark:text-white">{preReleaseConfig.title || "What's Coming Online"}</span>
                    </div>
                    <div className="space-y-2">
                      {preReleaseConfig.items?.map((item, index) => (
                        <div 
                          key={index}
                          className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/50 dark:bg-slate-800/50 border border-slate-200/50 dark:border-slate-700/50"
                        >
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            {item.label || '(Unnamed)'}
                          </span>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              item.status === 'Live' ? 'bg-green-500/10 text-green-600 border-green-500/20' :
                              item.status === 'Beta' ? 'bg-purple-500/10 text-purple-600 border-purple-500/20' :
                              item.status === 'Internal Testing' ? 'bg-amber-500/10 text-amber-600 border-amber-500/20' :
                              item.status === 'In Progress' ? 'bg-blue-500/10 text-blue-600 border-blue-500/20' :
                              'bg-slate-500/10 text-slate-600 border-slate-500/20'
                            }`}
                          >
                            {item.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Last Updated Info */}
                {preReleaseConfig.updated_at && (
                  <p className="text-xs text-muted-foreground dark:text-slate-500">
                    Last updated: {new Date(preReleaseConfig.updated_at).toLocaleString()}
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* User Management */}
        <Card className="dark:bg-slate-800/60 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="dark:text-white">User Management</CardTitle>
            <CardDescription className="dark:text-slate-400">Manage platform users and permissions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Button data-testid="add-user-button" onClick={() => navigate('/settings/users')}>
                <Users className="mr-2 h-4 w-4" />
                Add New User
              </Button>
            </div>
            <div className="border rounded-lg dark:border-slate-700">
              <Table>
                <TableHeader>
                  <TableRow className="dark:border-slate-700">
                    <TableHead className="dark:text-slate-300 w-16">#</TableHead>
                    <TableHead className="dark:text-slate-300">Name</TableHead>
                    <TableHead className="dark:text-slate-300">Email</TableHead>
                    <TableHead className="dark:text-slate-300">Role</TableHead>
                    <TableHead className="dark:text-slate-300">Status</TableHead>
                    <TableHead className="dark:text-slate-300">Last Login</TableHead>
                    <TableHead className="text-right dark:text-slate-300">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.slice(0, 10).map((user) => (
                    <TableRow key={user.id} className="dark:border-slate-700">
                      <TableCell className="font-mono text-sm text-muted-foreground dark:text-slate-400">
                        {user.employee_number || '-'}
                      </TableCell>
                      <TableCell className="font-medium dark:text-white">
                        {user.first_name} {user.last_name}
                      </TableCell>
                      <TableCell className="dark:text-slate-300">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize dark:border-slate-600 dark:text-slate-300">
                          {user.role.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.is_active ? (
                          <Badge className="bg-emerald-600 dark:bg-emerald-600">Active</Badge>
                        ) : (
                          <Badge variant="destructive">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground dark:text-slate-400">
                        {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/settings/users')} className="dark:hover:bg-slate-700">
                          <Settings className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* System Alerts */}
        <Card className="dark:bg-slate-800/60 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="dark:text-white">System Alerts</CardTitle>
            <CardDescription className="dark:text-slate-400">Important notifications and warnings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start space-x-3 p-4 border rounded-lg bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800">
                <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5" />
                <div>
                  <p className="font-medium text-emerald-900 dark:text-emerald-100">All Systems Operational</p>
                  <p className="text-sm text-emerald-700 dark:text-emerald-300">No issues detected in the last 24 hours</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 border rounded-lg bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800">
                <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-100">Database Backup Completed</p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">Last backup: 2 hours ago</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Engagement Widgets */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <OnboardingWidget onResume={() => {}} />
          <CompetitionZoneWidget />
        </div>
      </div>

      {/* Default Dashboard Editor Modal */}
      <Dialog open={dashboardEditorOpen} onOpenChange={setDashboardEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LayoutDashboard className="h-5 w-5 text-blue-500" />
              Default Dashboard Editor
            </DialogTitle>
            <DialogDescription>
              Configure the default dashboard layout and welcome message. Changes apply globally to all users.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Welcome Banner Editor */}
            <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded bg-blue-500/10">
                  <Rocket className="h-4 w-4 text-blue-500" />
                </div>
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">Welcome Banner Content</h3>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label htmlFor="welcome-title">Title</Label>
                  <Input
                    id="welcome-title"
                    value={welcomeConfig.title}
                    onChange={(e) => setWelcomeConfig({ ...welcomeConfig, title: e.target.value })}
                    placeholder="Welcome to RhinoShield Platform"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="welcome-description">Description</Label>
                  <Textarea
                    id="welcome-description"
                    value={welcomeConfig.description}
                    onChange={(e) => setWelcomeConfig({ ...welcomeConfig, description: e.target.value })}
                    placeholder="Your mission control center..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </div>
              
              {/* Preview */}
              <div className="mt-4">
                <Label className="text-xs text-muted-foreground">Preview</Label>
                <div className="mt-2 p-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg">
                  <h3 className="text-lg font-bold">{welcomeConfig.title || 'Welcome'}</h3>
                  <p className="text-sm text-blue-100 mt-1">{welcomeConfig.description || 'Add a description...'}</p>
                </div>
              </div>
            </div>

            {/* Layout Order Editor */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded bg-slate-500/10">
                  <GripVertical className="h-4 w-4 text-slate-500" />
                </div>
                <h3 className="font-semibold">Section Order &amp; Layout</h3>
              </div>
              
              <p className="text-sm text-muted-foreground">
                Drag to reorder sections. Set width to control how much horizontal space each section takes.
              </p>

              <DragDropContext onDragEnd={handleDashboardLayoutDragEnd}>
                <Droppable droppableId="dashboard-sections">
                  {(provided) => (
                    <div
                      className="space-y-2 p-3 border-2 border-dashed rounded-lg"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {editingLayout.map((section, index) => (
                        <Draggable key={section.section_id} draggableId={section.section_id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border ${
                                snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-500/50' : ''
                              }`}
                            >
                              <div {...provided.dragHandleProps}>
                                <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                              </div>
                              
                              <div className="flex-1">
                                <span className="font-medium">
                                  {SECTION_NAMES[section.section_id] || section.section_id}
                                </span>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <Label className="text-xs text-muted-foreground">Width:</Label>
                                <select
                                  value={section.w}
                                  onChange={(e) => updateSectionWidth(section.section_id, parseInt(e.target.value))}
                                  className="h-8 px-2 text-sm rounded border bg-background"
                                >
                                  <option value={4}>1/3 (4 cols)</option>
                                  <option value={6}>1/2 (6 cols)</option>
                                  <option value={8}>2/3 (8 cols)</option>
                                  <option value={12}>Full (12 cols)</option>
                                </select>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDashboardEditorOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveDashboardDefault} disabled={dashboardSaving} className="bg-blue-600 hover:bg-blue-700">
              {dashboardSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Globally
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
